import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { keyframes } from '@mui/system';
import BuildIcon from '@mui/icons-material/Build';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const dotBounce = keyframes`
	0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
	40%          { transform: scale(1);   opacity: 1; }
`;

const ThinkingIndicator = ({ label = 'A.M.A is thinking' }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
		<Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
			{label}
		</Typography>
		<Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
			{[0, 1, 2].map((i) => (
				<Box
					key={i}
					sx={{
						width: 6,
						height: 6,
						borderRadius: '50%',
						bgcolor: 'text.secondary',
						animation: `${dotBounce} 1.2s infinite ease-in-out`,
						animationDelay: `${i * 0.16}s`,
					}}
				/>
			))}
		</Box>
	</Box>
);

const ToolCallChip = ({ call }) => {
	let icon = <BuildIcon fontSize="small" />;
	let color = 'default';
	if (call.status === 'running') {
		icon = <CircularProgress size={12} />;
		color = 'info';
	} else if (call.status === 'ok') {
		icon = <CheckIcon fontSize="small" />;
		color = 'success';
	} else if (call.status === 'error') {
		icon = <ErrorOutlineIcon fontSize="small" />;
		color = 'error';
	}
	return (
		<Chip
			size="small"
			variant="outlined"
			color={color}
			icon={icon}
			label={call.name}
			sx={{ mr: 0.5, mb: 0.5 }}
		/>
	);
};

const Message = ({ message }) => {
	const isUser = message.role === 'user';

	return (
		<Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1.5 }}>
			<Paper
				elevation={0}
				sx={{
					px: 2,
					py: 1.25,
					maxWidth: '80%',
					bgcolor: isUser ? 'primary.main' : 'background.paper',
					color: isUser ? 'primary.contrastText' : 'text.primary',
					border: isUser ? 'none' : '1px solid',
					borderColor: 'divider',
					borderRadius: 2,
					'& p': { m: 0 },
					'& p + p': { mt: 1 },
					'& ul, & ol': { pl: 3, mt: 0.5, mb: 0.5 },
					'& pre': {
						bgcolor: 'action.hover',
						color: 'text.primary',
						p: 1,
						borderRadius: 1,
						overflowX: 'auto',
						fontSize: '0.85em',
					},
					'& code': {
						bgcolor: 'action.hover',
						color: 'text.primary',
						px: 0.5,
						py: 0.1,
						borderRadius: 0.5,
						fontSize: '0.9em',
					},
					'& table': {
						borderCollapse: 'collapse',
						my: 1,
					},
					'& th, & td': {
						border: '1px solid',
						borderColor: 'divider',
						px: 1,
						py: 0.5,
						fontSize: '0.9em',
					},
					'& th': { fontWeight: 600 },
				}}
			>
				{!isUser && message.toolCalls && message.toolCalls.length > 0 && (
					<Stack direction="row" flexWrap="wrap" sx={{ mb: message.content ? 1 : 0 }}>
						{message.toolCalls.map((c, i) => (
							<ToolCallChip key={i} call={c} />
						))}
					</Stack>
				)}
				{isUser ? (
					<Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
						{message.content}
					</Typography>
				) : message.content ? (
					<ReactMarkdown remarkPlugins={[remarkGfm]}>
						{message.content}
					</ReactMarkdown>
				) : !message.error ? (
					<ThinkingIndicator />
				) : null}
				{message.error && (
					<Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
						⚠ {message.error}
					</Typography>
				)}
			</Paper>
		</Box>
	);
};

export default Message;
